import { Button, Grid } from '@mui/material'
import Select from 'components/forms/Select'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import { EVENT_TYPES } from 'events/constants/EventsConstants'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { execByType, findStationType } from 'utils/StationUtils'
import SuperMultiAutocomplete from '../../../components/forms/SuperMultiAutocomplete'
import Checkbox from '../../../components/forms/Checkbox'
import { isNil, upperCase } from 'lodash'
import StationConstants from 'station/constants/StationConstants'

const FormGlobalEvents = ({ filter, onValidate }) => {
    const {
        piezometryCampaigns,
        pluviometryCampaigns,
        hydrometryCampaigns,
        qualityCampaigns,
        installationCampaigns,

        piezometersLight,
        hydrometricStations,
        qualitometersLight,
        installationsLight,

        stationTypes,
    } = useSelector(store => ({
        piezometryCampaigns: store.PiezometryReducer.piezometryCampaigns,
        pluviometryCampaigns: store.PluviometryReducer.pluviometryCampaigns,
        hydrometryCampaigns: store.HydrometryReducer.hydrometryCampaigns,
        qualityCampaigns: store.QualityReducer.qualityCampaigns,
        installationCampaigns: store.InstallationReducer.installationCampaigns,

        piezometersLight: store.PiezometryReducer.piezometersLight,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        qualitometersLight: store.QualityReducer.qualitometersLight,
        installationsLight: store.InstallationReducer.installationsLight,

        stationTypes: store.ReferencialReducer.stationTypes,
    }))

    const [type, setType] = useState(filter.type)
    const [stationKey, setStationKey] = useState(filter.stationKey)
    const [stationCode, setStationCode] = useState(filter.stationCode)
    const [campaign, setCampaign] = useState(filter.campaign)
    const [startDate, setStartDate] = useState(filter.startDate)
    const [endDate, setEndDate] = useState(filter.endDate)
    const [eventType, setEventType] = useState(filter.eventType)
    const [showTechnicalEvents, setShowTechnicalEvents] = useState(filter.showTechnicalEvents)

    const resetFilters = () => {
        setType(undefined)
        setStationKey(undefined)
        setStationCode(undefined)
        setCampaign(undefined)
        setStartDate(undefined)
        setEndDate(undefined)
        setEventType(undefined)
        setShowTechnicalEvents(false)
        onValidate({})
    }

    const filteredCampaigns = useMemo(() => execByType(type, {
        piezometry: () => piezometryCampaigns,
        hydrometry: () => hydrometryCampaigns,
        pluviometry: () => pluviometryCampaigns,
        quality: () => qualityCampaigns,
        installation: () => installationCampaigns,
        productionUnit: () => [],
        distributionUnit: () => [],
        default: () => [
            ...piezometryCampaigns,
            ...pluviometryCampaigns,
            ...hydrometryCampaigns,
            ...qualityCampaigns,
            ...installationCampaigns,
        ],
    }), [piezometryCampaigns, hydrometryCampaigns, pluviometryCampaigns, qualityCampaigns, installationCampaigns, type])

    const allStations = useMemo(() => [
        ...piezometersLight,
        ...qualitometersLight,
        ...installationsLight,
        ...hydrometricStations,
    ].map(({ id, code, typeName, name }) => ({
        id,
        code,
        typeName,
        name: `[${upperCase(StationConstants.STATION_TYPE_NAME_TRONCATE[typeName])}] [${code}] - ${name || ''}`,
        key: `${id}-${code}-${typeName}`,
    })), [hydrometricStations, installationsLight, piezometersLight, qualitometersLight])

    const filteredStations = useMemo(() => {
        if (isNil(type) || type.length === 0) {
            return allStations
        }
        const stationTypesFormat = type.map(t => findStationType(t))
        const typeNames = stationTypesFormat.map(st => st.type)
        return allStations.filter(s => typeNames.includes(s.typeName))
    }, [allStations, type])

    const stationTypesFormat = useMemo(() => stationTypes.filter(s => stationKey ? type.includes(s.code) : true).map(s => ({
        code: s.code,
        type: s.name,
        name: i18n[s.i18n],
    })), [stationTypes, stationKey, type])

    const eventTypes = EVENT_TYPES.map(evt => ({
        code: evt.code,
        label: i18n[evt.label],
    }))

    return (
        <Grid container size={12} spacing={1} sx={{ padding: 1 }}>
            <Grid container size={12}>
                <Grid size={3}>
                    <SuperMultiAutocomplete
                        label={i18n.stationType}
                        options={stationTypesFormat}
                        values={type}
                        onChange={v => setType(v)}
                        multiple
                        keyValue={'code'}
                    />
                </Grid>
                <Grid size={6}>
                    <Select
                        label={i18n.station}
                        options={filteredStations}
                        value={stationKey}
                        onChange={(key) => {
                            if (isNil(key)) {
                                setStationCode(undefined)
                                setStationKey(undefined)
                                return
                            }
                            const id = Number(key.split('-')[0])
                            const typeName = key.split('-')[2]
                            setType([StationConstants.STATION_TYPE_CONSTANT[typeName]])
                            setStationCode(id)
                            setStationKey(key)
                        }}
                        keyValue='key'
                        clearFunction
                    />
                </Grid>
                <Grid size={3}>
                    <Select
                        label={i18n.campaign}
                        options={filteredCampaigns}
                        value={campaign}
                        onChange={v => setCampaign(v)}
                        clearFunction
                    />
                </Grid>
            </Grid>
            <Grid container size={12} alignItems={'center'}>
                <Grid size={3}>
                    <SimpleDatePicker
                        id='startDate'
                        label={i18n.startDate}
                        value={startDate}
                        onChange={(v) => setStartDate(v)}
                    />
                </Grid>
                <Grid size={3}>
                    <SimpleDatePicker
                        id='endDate'
                        label={i18n.endDate}
                        value={endDate}
                        onChange={(v) => setEndDate(v)}
                    />
                </Grid>
                <Grid size={3}>
                    <SuperMultiAutocomplete
                        label={i18n.eventTypes}
                        options={eventTypes}
                        values={eventType}
                        onChange={v => setEventType(v)}
                        multiple
                        keyValue={'code'}
                    />
                </Grid>
                <Grid size={9}>
                    <Checkbox
                        checked={ showTechnicalEvents }
                        label={ i18n.showTechnicalEvents }
                        onChange={ setShowTechnicalEvents }
                    />
                </Grid>
                <Grid container size={1.5}>
                    <Button
                        variant='outlined'
                        onClick={resetFilters}
                        fullWidth
                    >
                        {i18n.reinit}
                    </Button>
                </Grid>
                <Grid container size={1.5}>
                    <Button
                        variant='contained'
                        onClick={() => onValidate({ type, stationCode, campaign, startDate, endDate, eventType, showTechnicalEvents })}
                        fullWidth
                    >
                        {i18n.search}
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    )
}

FormGlobalEvents.propTypes = {
    filter: PropTypes.objectOf({
        type: PropTypes.arrayOf(PropTypes.number),
        stationCode: PropTypes.number,
        campaign: PropTypes.number,
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        eventType: PropTypes.arrayOf(PropTypes.number),
    }),
    onValidate: PropTypes.func,
    stations: PropTypes.objectOf({
        id: PropTypes.number,
        code: PropTypes.string,
        typeName: PropTypes.string,
        name: PropTypes.string,
    }),
}

export default FormGlobalEvents